import { create } from "zustand";
import { Campaign, CampaignOwner, Stage } from "../../types/types";
import { useBriefStore } from "./briefStore";
import { toast } from "sonner";

interface CampaignState {
  campaign: Campaign | null;
  campaignId: string | null;
  campaign_owner: CampaignOwner;
  isLoading: boolean;
  deleteLoading: boolean;
  updatingCampaignField: boolean;
  error: string | null;
  use_dub: boolean;
  fetchCampaign: (
    campaign_id: string,
    organizationId: string,
    token: string,
    onLimitReached?: (data: {
      feature: string;
      current: number;
      limit: number;
    }) => void
  ) => Promise<Campaign | undefined>;
  refreshCampaign: (
    campaign_id: string,
    organizationId: string,
    token: string
  ) => Promise<void>;
  deleteCampaign: (
    campaignId: string,
    token: string,
    onLimitReached?: (data: {
      feature: string;
      current: number;
      limit: number;
    }) => void
  ) => Promise<void>;
  updateCampaignField: (
    field: string,
    value: string | boolean | number,
    campaignId: string,
    token: string
  ) => Promise<void>;
  updateCampaignOwner: (
    campaignId: string,
    organizationId: string,
    selection: {
      user_id: string;
      external_account_id: string;
      name: string;
      email: string;
      imageUrl: string;
      provider: string;
    },
    token: string
  ) => Promise<{ success: boolean; error?: string }>;
  updateCampaignAutopilot: (
    campaignId: string,
    newValue: boolean,
    token: string
  ) => Promise<void>;
  updateStages: (
    campaignId: string,
    stages: Stage[],
    token: string
  ) => Promise<void>;
  advanceCreatorStage: (
    campaignCreatorId: string,
    token: string
  ) => Promise<boolean>;
  setCampaign: (campaign: Campaign) => void;
  setCampaignId: (campaignId: string) => void;
  updateCampaign: (updatedCampaign: Partial<Campaign>) => void;
  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const handleApiResponse = async (
  response: Response,
  onLimitReached?: (data: {
    feature: string;
    current: number;
    limit: number;
  }) => void
) => {
  const data = await response.json();

  if (response.status === 429) {
    onLimitReached?.({
      feature: data.error?.match(/'([^']+)'/)?.[1] || "Feature",
      current: data.current || 0,
      limit: data.limit || 0,
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(data.error || "API Error");
  }

  return data;
};

export const useCampaignStore = create<CampaignState>((set) => ({
  campaign: null,
  campaignId: null,
  campaign_owner: { name: "", email: "", avatar: "" },
  isLoading: false,
  deleteLoading: false,
  updatingCampaignField: false,
  error: null,
  use_dub: false,

  fetchCampaign: async (
    campaign_id: string,
    organizationId: string,
    token: string,
    onLimitReached?: (data: {
      feature: string;
      current: number;
      limit: number;
    }) => void
  ) => {
    set({ isLoading: true, error: null });
    const { setBrief, setIncludeCreatorPortal } = useBriefStore.getState();
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/get`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaign_id,
          organization_id: organizationId,
        }),
      });

      const data = await handleApiResponse(response, onLimitReached);

      if (!data) {
        set({ isLoading: false });
        return;
      }

      set({
        campaign: data.campaign,
        campaignId: campaign_id,
        campaign_owner: data.campaign?.campaign_owner,
        isLoading: false,
        use_dub: data.campaign?.use_dub ?? false,
      });
      
      // console.log("Fetched campaign:", data.campaign);

      setBrief(data.campaign?.brief || null);
      setIncludeCreatorPortal(data.campaign?.brief?.include_creator_portal || false);

      return data.campaign;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  // Silent re-sync (no isLoading flip, brief store untouched) — used after
  // Amy-v2 config saves so the pipeline editor reflects server truth
  // without a page-level loading state.
  refreshCampaign: async (campaign_id: string, organizationId: string, token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/get`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaign_id,
          organization_id: organizationId,
        }),
      });
      const data = await handleApiResponse(response);
      if (data?.campaign) {
        set({
          campaign: data.campaign,
          campaign_owner: data.campaign?.campaign_owner,
          use_dub: data.campaign?.use_dub ?? false,
        });
      }
    } catch {
      // Silent — the next full fetch reconciles.
    }
  },

  deleteCampaign: async (
    campaignId: string,
    token: string,
    onLimitReached?: (data: {
      feature: string;
      current: number;
      limit: number;
    }) => void
  ) => {
    set({ deleteLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_id: campaignId }),
      });

      const data = await handleApiResponse(response, onLimitReached);

      if (!data) {
        set({ deleteLoading: false });
        return;
      }

      if (data.success) {
        set({ campaign: null, deleteLoading: false });
      } else {
        set({ error: data.message, deleteLoading: false });
      }
    } catch (error) {
      set({ error: (error as Error).message, deleteLoading: false });
    }
  },

  updateCampaignField: async (
    field: string,
    // Objects/arrays travel JSON-stringified (parsed back below); booleans and
    // numbers travel as-is so Mongo stores the real type, never "false".
    value: string | boolean | number,
    campaignId: string,
    token: string
  ) => {
    set({ updatingCampaignField: true, error: null });
    
    const updatePromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/updateCampaignField`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_id: campaignId, field, value }),
      }); 
      
      const data = await handleApiResponse(response);
      if (!data) {
        throw new Error("Failed to update campaign field");
      }

      // Parse JSON for fields that are stored as objects/arrays on the backend
      const parsedValue = (field === "email_limits" || field === "fields" || field === "schedule" || field === "cc_emails") ? JSON.parse(value as string) : value;

      set((state) => ({
        updatingCampaignField: false,
        campaign: state.campaign ? {
          ...state.campaign,
          [field]: parsedValue
        } : null
      }));
      return data;
    })();

    toast.promise(updatePromise, {
      loading: "Updating campaign field...",
      success: "Campaign field updated successfully!",
      error: "Failed to update campaign field",
    });

    try {
      await updatePromise;
    } catch (error) {
      set({ updatingCampaignField: false, error: (error as Error).message });
      console.error("Error updating campaign field:", error);
    }
  },

  updateCampaignOwner: async (
    campaignId: string,
    organizationId: string,
    selection: {
      user_id: string;
      external_account_id: string;
      name: string;
      email: string;
      imageUrl: string;
      provider: string;
    },
    token: string
  ) => {
    set({ updatingCampaignField: true, error: null });

    const updatePromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/updateCampaignOwner`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          organization_id: organizationId,
          user_id: selection.user_id,
          external_account_id: selection.external_account_id,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to update campaign owner");
      }

      set((state) => ({
        updatingCampaignField: false,
        campaign: state.campaign
          ? {
              ...state.campaign,
              user_id: selection.user_id,
              external_account_id: selection.external_account_id,
              campaign_owner: {
                name: selection.name,
                email: selection.email,
                avatar: selection.imageUrl,
                provider: selection.provider,
              },
            }
          : null,
      }));
      return data;
    })();

    toast.promise(updatePromise, {
      loading: "Updating campaign owner...",
      success: "Campaign owner updated!",
      error: (err: Error) => err.message || "Failed to update campaign owner",
    });

    try {
      await updatePromise;
      return { success: true };
    } catch (error) {
      const message = (error as Error).message;
      set({ updatingCampaignField: false, error: message });
      return { success: false, error: message };
    }
  },

  updateCampaignAutopilot: async (
    campaignId: string,
    newValue: boolean,
    token: string
  ) => {
    set({ updatingCampaignField: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/updateCampaignAutopilot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_id: campaignId, new_value: newValue }),
      });

      const data = await handleApiResponse(response);
      if (!data) {
        return;
      }
      set((state) => ({
        updatingCampaignField: false,
        campaign: state.campaign ? {
          ...state.campaign,
          autopilot: newValue
        } : null
      }));
      toast.success("Campaign autopilot updated successfully!");
    } catch (error) {
      set({ updatingCampaignField: false, error: (error as Error).message });
      console.error("Error updating campaign autopilot:", error);
    }
  },

  updateStages: async (
    campaignId: string,
    stages: Stage[],
    token: string
  ) => {
    const updatePromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/updateStages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_id: campaignId, stages }),
      });

      const data = await handleApiResponse(response);
      if (!data) throw new Error("Failed to update stages");

      set((state) => ({
        campaign: state.campaign ? { ...state.campaign, stages } : null,
      }));
      return data;
    })();

    toast.promise(updatePromise, {
      loading: "Saving custom statuses...",
      success: "Custom statuses saved successfully!",
      error: "Failed to save custom statuses",
    });

    try {
      await updatePromise;
    } catch (error) {
      console.error("Error updating stages:", error);
    }
  },

  advanceCreatorStage: async (
    campaignCreatorId: string,
    token: string
  ): Promise<boolean> => {
    const advancePromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/advanceCreatorStage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_creator_id: campaignCreatorId }),
      });

      const data = await handleApiResponse(response);
      if (!data) throw new Error("Failed to advance stage");
      return data;
    })();

    toast.promise(advancePromise, {
      loading: "Advancing status...",
      success: "Creator advanced to next status!",
      error: "Failed to advance status",
    });

    try {
      await advancePromise;
      return true;
    } catch {
      return false;
    }
  },

  setCampaign: (campaign: Campaign) => set({ campaign }),

  setCampaignId: (campaignId: string) => set({ campaignId: campaignId }),

  updateCampaign: (updatedCampaign: Partial<Campaign>) => {
    set((state) => ({
      campaign: state.campaign
        ? { ...state.campaign, ...updatedCampaign }
        : null,
    }));
  },

  reset: () =>
    set({
      campaign: null,
      campaignId: null,
      isLoading: false,
      deleteLoading: false,
      error: null,
      use_dub: false,
    }),
}));
